import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { MinusIcon, PlusIcon } from '@air/design-icons';

import { MAX_ZOOM, MIN_ZOOM } from '../constants';

const ZOOM_PERCENTAGE_SCALE = 100;
const MIN_ZOOM_PERCENTAGE = MIN_ZOOM * ZOOM_PERCENTAGE_SCALE;
const MAX_ZOOM_PERCENTAGE = MAX_ZOOM * ZOOM_PERCENTAGE_SCALE;

export type ToolbarZoomControlsProps = {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomTo: (percentage: number) => void;
  zoomPercentage: number;
};

export const ToolbarZoomControls = memo(
  ({ onZoomIn, onZoomOut, onZoomTo, zoomPercentage }: ToolbarZoomControlsProps) => {
    const [inputValue, setInputValue] = useState(String(zoomPercentage));
    const isFocusedRef = useRef<boolean>(false);
    const isEscapingRef = useRef<boolean>(false);
    const isSubmittingRef = useRef<boolean>(false);

    useEffect(() => {
      // biome-ignore lint/suspicious/noUnnecessaryConditions: `.current` is written in the focus/blur handlers below; Biome only sees the initial `false`.
      if (!isFocusedRef.current) {
        setInputValue(String(zoomPercentage));
      }
    }, [zoomPercentage]);

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    }, []);

    const handleFocus = useCallback((e: FocusEvent<HTMLInputElement>) => {
      isFocusedRef.current = true;
      e.currentTarget.select();
    }, []);

    const handleBlur = useCallback(() => {
      isFocusedRef.current = false;

      // biome-ignore lint/suspicious/noUnnecessaryConditions: `.current` is written in the Escape branch of the keydown handler below; Biome only sees the initial `false`.
      if (isEscapingRef.current) {
        isEscapingRef.current = false;
        return;
      }

      // biome-ignore lint/suspicious/noUnnecessaryConditions: `.current` is written in the Enter branch of the keydown handler below; Biome only sees the initial `false`.
      if (isSubmittingRef.current) {
        isSubmittingRef.current = false;
        return;
      }

      const parsed = Number.parseInt(inputValue, 10);

      if (!Number.isNaN(parsed)) {
        const clamped = Math.min(MAX_ZOOM_PERCENTAGE, Math.max(MIN_ZOOM_PERCENTAGE, parsed));
        onZoomTo(clamped);
        setInputValue(String(clamped));
      } else {
        setInputValue(String(zoomPercentage));
      }
    }, [inputValue, onZoomTo, zoomPercentage]);

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          const parsed = Number.parseInt(inputValue, 10);

          if (!Number.isNaN(parsed)) {
            const clamped = Math.min(MAX_ZOOM_PERCENTAGE, Math.max(MIN_ZOOM_PERCENTAGE, parsed));
            onZoomTo(clamped);
            setInputValue(String(clamped));
            isSubmittingRef.current = true;
          }

          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          e.stopPropagation();
          isEscapingRef.current = true;
          setInputValue(String(zoomPercentage));
          e.currentTarget.blur();
        }
      },
      [inputValue, onZoomTo, zoomPercentage],
    );

    return (
      <div className="flex items-center gap-1 rounded-8 border ads:border-ads-gray-5 border-gray-5 px-2 py-1">
        <button
          aria-label="Zoom Out"
          className="flex items-center justify-center ads:text-icon-emphasis text-gray-12 hover:ads:text-icon-emphasis hover:text-gray-12"
          onClick={onZoomOut}
          title="Zoom Out"
          type="button"
        >
          <MinusIcon size="xs" />
        </button>
        <input
          aria-label="Zoom percentage"
          className="w-8 bg-transparent text-center font-semibold ads:text-primary text-12 text-gray-12 outline-none focus-visible:rounded-2 focus-visible:ads:ring-focus focus-visible:ring-1 focus-visible:ring-blue-9"
          data-testid="CANVAS_TOOLBAR_ZOOM_INPUT"
          inputMode="numeric"
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          type="text"
          value={inputValue}
        />
        <span className="font-semibold ads:text-primary text-12 text-gray-12">%</span>
        <button
          aria-label="Zoom In"
          className="flex items-center justify-center ads:text-icon-emphasis text-gray-12 hover:ads:text-icon-emphasis hover:text-gray-12"
          onClick={onZoomIn}
          title="Zoom In"
          type="button"
        >
          <PlusIcon size="xs" />
        </button>
      </div>
    );
  },
);

ToolbarZoomControls.displayName = 'ToolbarZoomControls';
